// Payment Mode Service
// Always uses live payments with Razorpay

class PaymentModeService {
  constructor() {
    this.mode = 'live'; // Always live mode
    this.razorpayConfig = null;
    this.listeners = [];
    this.initializeFromCSV();
  }

  // Initialize from CSV file
  async initializeFromCSV() {
    try {
      // Add cache-busting timestamp to force reload
      const response = await fetch(`/rzp-key.csv?t=${Date.now()}`);
      if (response.ok) {
        const csvText = await response.text();
        const lines = csvText.trim().split('\n');
        
        if (lines.length >= 2) {
          const headers = lines[0].split(',');
          const data = lines[1].split(',');
          
          const keyIdIndex = headers.findIndex(h => h.trim() === 'key_id');
          const keySecretIndex = headers.findIndex(h => h.trim() === 'key_secret');
          
          if (keyIdIndex !== -1 && keySecretIndex !== -1) {
            const keyId = data[keyIdIndex]?.trim();
            const keySecret = data[keySecretIndex]?.trim();
            
            if (keyId && keySecret) {
              this.setMode('live', {
                keyId: keyId,
                keySecret: keySecret
              });
              return;
            }
          }
        }
      }
    } catch (error) {
      // Failed to load rzp-key.csv, retrying...
      
      // Retry after 2 seconds
      setTimeout(() => {
        this.retryCSVLoad();
      }, 2000);
    }
  }

  // Retry loading CSV file
  async retryCSVLoad() {
    try {
      // Add cache-busting timestamp to force reload
      const response = await fetch(`/rzp-key.csv?t=${Date.now()}`);
      if (response.ok) {
        const csvText = await response.text();
        const lines = csvText.trim().split('\n');
        
        if (lines.length >= 2) {
          const headers = lines[0].split(',');
          const data = lines[1].split(',');
          
          const keyIdIndex = headers.findIndex(h => h.trim() === 'key_id');
          const keySecretIndex = headers.findIndex(h => h.trim() === 'key_secret');
          
          if (keyIdIndex !== -1 && keySecretIndex !== -1) {
            const keyId = data[keyIdIndex]?.trim();
            const keySecret = data[keySecretIndex]?.trim();
            
            if (keyId && keySecret) {
              this.setMode('live', {
                keyId: keyId,
                keySecret: keySecret
              });
              return;
            }
          }
        }
      }
    } catch (error) {
      // Failed to load rzp-key.csv on retry
    }
  }

  // Set payment mode (always live)
  setMode(mode, config = null) {
    if (config) {
      this.razorpayConfig = config;
    }
    this.mode = 'live'; // Always live mode
    this.notifyListeners();
  }

  // Get current mode (always live)
  getMode() {
    return 'live';
  }

  // Check if in live mode (always true)
  isLiveMode() {
    return true;
  }

  // Check if in demo mode (always false)
  isDemoMode() {
    return false;
  }

  // Get Razorpay config
  getRazorpayConfig() {
    return this.razorpayConfig;
  }

  // Add listener for mode changes
  addListener(callback) {
    this.listeners.push(callback);
  }

  // Remove listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  // Notify all listeners
  notifyListeners() {
    this.listeners.forEach(listener => listener(this.mode));
  }

  // Initialize from settings (always live mode)
  initializeFromSettings(settings) {
    if (settings.razorpayKeyId && settings.razorpayKeySecret) {
      this.setMode('live', {
        keyId: settings.razorpayKeyId,
        keySecret: settings.razorpayKeySecret
      });
    } else {
      // Try to load from CSV if settings don't have keys
      this.initializeFromCSV().catch(error => {
        // Could not load from CSV, payment system may not work
      });
    }
  }
}

// Create singleton instance
const paymentModeService = new PaymentModeService();

export default paymentModeService;
